// api/customers.ts
import { apiClient, type PageData } from './client'
import type { CrmebUser } from './admin-order'
import type {
	Customer,
	CustomerFilter,
	CheckContactResponse,
	CheckContactBatchResponse,
	CreateCustomerRequest,
	UpdateCustomerRequest,
	TransferCustomerRequest,
	CheckContactRequest,
	CheckContactBatchRequest,
} from '@/types/customer'

// ============================================
// 客户管理 API
// ============================================

export const customersApi = {
	/**
	 * 获取客户列表
	 */
	list: async (params?: {
		page?: number
		page_size?: number
	} & CustomerFilter): Promise<PageData<Customer>> => {
		const searchParams = new URLSearchParams()
		if (params?.page) searchParams.set('page', String(params.page))
		if (params?.page_size) searchParams.set('page_size', String(params.page_size))
		if (params?.name) searchParams.set('name', params.name)
		if (params?.phone) searchParams.set('phone', params.phone)
		if (params?.wechat) searchParams.set('wechat', params.wechat)
		if (params?.user_id) searchParams.set('user_id', String(params.user_id))
		if (params?.tracker_id) searchParams.set('tracker_id', String(params.tracker_id))
		if (params?.user_ids && params.user_ids.length > 0) {
			searchParams.set('user_ids', params.user_ids.join(','))
		}
		if (params?.tracker_ids && params.tracker_ids.length > 0) {
			searchParams.set('tracker_ids', params.tracker_ids.join(','))
		}
		if (typeof params?.blacklist === 'boolean') {
			searchParams.set('blacklist', String(params.blacklist))
		}
		if (params?.scope) searchParams.set('scope', params.scope)
		if (
			params?.scope === 'department' &&
			typeof params.department_id === 'number' &&
			Number.isInteger(params.department_id) &&
			params.department_id > 0
		) {
			searchParams.set('department_id', String(params.department_id))
		}
		if (params?.start_date) searchParams.set('start_date', params.start_date)
		if (params?.end_date) searchParams.set('end_date', params.end_date)
		if (typeof params?.category_id === 'number' && params.category_id > 0) {
			searchParams.set('category_id', String(params.category_id))
		}
		if (typeof params?.vip_level === 'number') searchParams.set('vip_level', String(params.vip_level))

		const queryString = searchParams.toString()
		const url = queryString ? `/customers?${queryString}` : '/customers'

		const response = await apiClient.get<PageData<Customer>>(url)
		return response.data
	},

	/**
	 * 获取客户详情
	 */
	get: async (id: number): Promise<Customer> => {
		const response = await apiClient.get<Customer>(`/customers/${id}`)
		return response.data
	},

	/**
	 * 创建客户
	 */
	create: async (data: CreateCustomerRequest): Promise<Customer> => {
		const response = await apiClient.post<Customer>('/customers', data)
		return response.data
	},

	/**
	 * 更新客户
	 */
	update: async (id: number, data: UpdateCustomerRequest): Promise<void> => {
		await apiClient.put(`/customers/${id}`, data)
	},

	/**
	 * 删除客户
	 */
	delete: async (id: number): Promise<void> => {
		await apiClient.delete(`/customers/${id}`)
	},

	/**
	 * 检查单个联系方式
	 */
	checkContact: async (data: CheckContactRequest): Promise<CheckContactResponse> => {
		const response = await apiClient.post<CheckContactResponse>('/customers/check', data)
		return response.data
	},

	/**
	 * 批量检查联系方式
	 */
	checkContactBatch: async (data: CheckContactBatchRequest): Promise<CheckContactBatchResponse> => {
		const response = await apiClient.post<CheckContactBatchResponse>('/customers/check/batch', data)
		return response.data
	},

	/**
	 * 转移客户
	 */
	transfer: async (data: TransferCustomerRequest): Promise<void> => {
		await apiClient.post('/customers/transfer', data)
	},

	/**
	 * 准备客户手动录单
	 */
	prepareManualOrder: async (id: number): Promise<CrmebUser> => {
		const response = await apiClient.post<CrmebUser>(`/customers/${id}/prepare-manual-order`)
		return response.data
	},
}
